<template>
  <div id="catalog-items-edit">
    <nav class="breadcrumb">
      <ul>
        <li>
          <router-link class="p-0 m-0" to="/catalog-item"><a>Catalog Items</a></router-link>
        </li>
        <li class="is-active">
          <router-link class="p-0 m-0" to="/catalog-item">
            <a>{{ title | truncate(200) }}</a>
          </router-link>
        </li>
      </ul>
    </nav>
    <b-modal
      v-model="isAddNewActive"
      trap-focus
      :full-screen="isMobile"
      aria-role="dialog"
      aria-modal
      :destroy-on-hide="false"
    >
      <template #default>
        <div class="modal-card">
          <header class="modal-card-head">Add new {{ addNewType }}</header>
          <section class="modal-card-body">
            <b-field label="Value">
              <b-input v-model="addNewValue" expanded required></b-input>
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <b-button size="is-small" label="Close" type="is-danger" @click="closeModal()"></b-button>
            <b-button
              size="is-small"
              label="Add"
              type="is-success"
              @click="closeModal(true)"
              :disabled="!isAddNewValueValid"
            ></b-button>
          </footer>
        </div>
      </template>
    </b-modal>
    <div v-if="loading">
      <div>Loading</div>
      <b-progress></b-progress>
    </div>
    <form class="block" v-else @submit.prevent="save">
      <div class="header">
        <template v-if="isNew">{{ title }}</template>
        <span v-else-if="isImportedItem" class="catalog-item-edit__title-mark">
          <span class="catalog-item-edit__title-text">{{ title }}</span>
          <span class="catalog-item-edit__import-wrap">
            <b-tooltip label="This catalog item was created by import" position="is-bottom" multilined>
              <b-icon icon="file-import" type="is-success" size="is-small" class="catalog-item-edit__import-icon" />
            </b-tooltip>
          </span>
        </span>
        <template v-else>{{ title }}</template>
      </div>
      <div v-if="headerLastUpdated" class="catalog-item-edit__last-updated catalog-item-edit__last-updated--header">
        Last Updated: {{ headerLastUpdated | formatDate }}
      </div>
      <div class="columns" v-if="!isNew">
        <div class="column is-narrow">
          <b-field>
            <b-switch v-model="item.isActive" type="is-success">
              <b-tag :type="item.isActive ? 'is-success' : 'is-light'">
                {{ item.isActive ? 'Active' : 'Inactive' }}
              </b-tag>
            </b-switch>
          </b-field>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <b-field label="Name">
            <b-input v-model="item.name"></b-input>
          </b-field>
        </div>
        <div class="column is-narrow">
          <b-field label="Type">
            <b-select v-model="item.type">
              <option v-for="type in types" :value="type.value" :key="type.value">{{ type.label }}</option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-narrow" v-if="!isPermit">
          <b-field label="Quality">
            <b-select v-model="item.quality">
              <option v-for="q in qualities" :value="q.value" :key="q.value">{{ q.label }}</option>
            </b-select>
          </b-field>
        </div>
      </div>
      <div class="columns" v-if="!isPermit">
        <div class="column">
          <b-field label="Description">
            <textarea class="textarea" v-model="item.description"></textarea>
          </b-field>
        </div>
      </div>
      <div class="columns" v-if="!isPermit">
        <div class="column">
          <b-field>
            <b-switch v-model="item.isTaxable">Taxable</b-switch>
          </b-field>
        </div>
        <div class="column" v-if="isEquipment">
          <b-field>
            <b-switch v-model="item.checkedIn">Checked In</b-switch>
          </b-field>
        </div>
      </div>
      <div v-if="isLabor" class="labor-pricing-section">
        <div class="my-3 is-size-5">Labor pricing</div>
        <div class="group my-3">
          <div class="is-size-6 has-text-grey mb-2">Rates & overhead</div>
          <div class="columns">
            <div class="column">
              <b-field label="Employee Productivity Price ($/hr)">
                <b-input
                  v-model.number="item.priceConfiguration.employeeProductivityPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  expanded
                ></b-input>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Man Labor Hour Overhead Price ($/mlh)">
                <b-input
                  v-model.number="item.priceConfiguration.mlhOverheadPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  expanded
                ></b-input>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Local Overhead Price ($/hr)">
                <b-input
                  v-model.number="item.priceConfiguration.localOverheadPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  expanded
                ></b-input>
              </b-field>
            </div>
          </div>
        </div>
        <div class="group my-3">
          <div class="is-size-6 has-text-grey mb-2">Burden</div>
          <div class="columns">
            <div class="column">
              <b-field label="% Labor Burden">
                <b-input
                  v-model.number="item.priceConfiguration.laborBurdenPercent"
                  type="number"
                  step="0.01"
                  min="0"
                  expanded
                ></b-input>
              </b-field>
            </div>
            <div class="column">
              <b-field label="Labor Burden Price ($)">
                <b-input
                  v-model.number="item.priceConfiguration.laborBurdenPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  expanded
                ></b-input>
              </b-field>
            </div>
          </div>
        </div>
      </div>
      <div class="columns">
        <div class="column" v-if="!isPermit">
          <b-field label="Margin">
            <b-input
              icon="percentage"
              v-model.number="item.priceConfiguration.marginPercentage"
              type="number"
              step="0.01"
              min="0"
            ></b-input>
          </b-field>
        </div>
        <div class="column">
          <b-field :label="isPermit ? 'Cost' : 'Cost Per Unit'">
            <b-input
              icon="dollar-sign"
              v-model.number="item.priceConfiguration.cost"
              type="number"
              step="0.01"
              min="0"
            ></b-input>
          </b-field>
          <div v-if="vendorCostLastUpdated" class="catalog-item-edit__last-updated">
            Last Updated: {{ vendorCostLastUpdated | formatDate }}
          </div>
        </div>
        <div class="column">
          <b-field label="Cost To Customer">
            <b-input icon="dollar-sign" v-model="cost.markupCost" disabled></b-input>
          </b-field>
          <div v-if="costToCustomerLastUpdated" class="catalog-item-edit__last-updated">
            Last Updated: {{ costToCustomerLastUpdated | formatDate }}
          </div>
        </div>
      </div>
      <div v-if="isFpv">
        <div class="my-4 is-size-3">FPVM</div>
        <div class="columns">
          <div class="column" v-for="field in currentTypeFields" :key="field.key">
            <b-field grouped>
              <b-field :label="field.label" expanded>
                <b-select v-model="item[field.fieldKey]" :loading="$asyncComputed.detail.updating" expanded>
                  <option v-for="d in detail[field.key]" :value="d" :key="d">{{ d }}</option>
                </b-select>
                <b-button class="is-success" label="Add New" @click="openModal(field.key)"></b-button>
              </b-field>
            </b-field>
          </div>
        </div>
      </div>
      <div v-if="isAppliance">
        <div class="my-4 is-size-3">Appliance</div>
        <div class="columns">
          <div class="column" v-for="(field, i) in currentTypeFields" :key="i">
            <b-field grouped>
              <b-field :label="field.label" expanded>
                <b-select v-model="item[field.fieldKey]" :loading="$asyncComputed.detail.updating" expanded>
                  <option v-for="d in detail[field.key]" :value="d" :key="d">{{ d }}</option>
                </b-select>
                <b-button class="is-success" label="Add New" @click="openModal(field.key)"></b-button>
              </b-field>
            </b-field>
          </div>
        </div>
      </div>
      <div v-if="isFixture">
        <div class="my-4 is-size-3">Fixture</div>
        <div class="columns">
          <div class="column" v-for="field in currentTypeFields" :key="field.key">
            <b-field grouped>
              <b-field :label="field.label" expanded>
                <b-select v-model="item[field.fieldKey]" :loading="$asyncComputed.detail.updating" expanded>
                  <option v-for="d in detail[field.key]" :value="d" :key="d">{{ d }}</option>
                </b-select>
                <b-button class="is-success" label="Add New" @click="openModal(field.key)"></b-button>
              </b-field>
            </b-field>
          </div>
        </div>
      </div>
      <div v-if="!isPermit">
        <div class="supplier-header">
          <div class="my-4 is-size-3">Supplier</div>
        </div>
        <div class="columns is-vcentered" v-if="!hasSupplier">
          <div class="column">
            <b-message type="is-danger">
              <span class="mr-3">No supplier is assigned to this item.</span>
              <b-button class="is-small" @click="isAddSupplierModalActive = true">Assign Supplier</b-button>
            </b-message>
          </div>
        </div>
        <div v-else>
          <div class="grouping">
            <div
              class="p-3 m-0 columns is-gapless is-vcentered"
              v-for="(s, index) in displaySuppliers"
              :key="s.id"
              :class="{ 'is-even': index % 2 === 0, 'is-odd': index % 2 !== 0 }"
            >
              <div class="column is-9">
                <div class="columns">
                  <div class="column is-narrow">
                    <span class="field-name text-lg">{{ s.name }}</span>
                  </div>
                </div>
                <div class="columns">
                  <div class="column is-narrow">
                    <span class="field-name min-w-2">Sku</span>
                    <span>{{ s.sku }}</span>
                  </div>
                  <div class="column is-narrow">
                    <span class="field-name">Cost</span>
                    <span>${{ s.cost }}</span>
                  </div>

                  <div class="column is-narrow">
                    <span class="field-name">Priced At</span>
                    <span>{{ formatSupplierPricedAt(s.pricedAt) }}</span>
                  </div>
                </div>
              </div>
              <div class="column is-3">
                <div class="mr-1 is-pulled-right">
                  <div class="buttons">
                    <b-button
                      class="is-small is-danger"
                      icon-left="trash"
                      :loading="unassigningSupplier"
                      @click="unassignSupplier()"
                    ></b-button>
                    <b-button class="is-small is-info" icon-left="edit" @click="onEditSupplier(s)"></b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-3 buttons is-pulled-right">
        <b-button v-if="canDelete" class="is-danger is-outlined" icon-left="trash" @click="deleteItem">Delete</b-button>
        <b-button class="is-danger" @click="goBack">Cancel</b-button>
        <button class="button is-success" type="submit">Save</button>
      </div>
    </form>
    <b-modal v-model="isEditSupplierModalActive" has-modal-card trap-focus>
      <template #default>
        <div class="modal-card">
          <header class="modal-card-head">{{ editSupplier.name }}</header>
          <section class="modal-card-body">
            <div class="columns">
              <div class="column is-fullwidth">
                <b-field label="Description">
                  <b-input type="textarea" v-model="editSupplier.description" label="Description" rows="3" required />
                </b-field>
                <b-field label="Sku">
                  <b-input v-model="editSupplier.sku" label="Sku" required />
                </b-field>
                <b-field label="Cost" required>
                  <b-input
                    type="number"
                    step="0.01"
                    min="0"
                    icon="dollar-sign"
                    v-model.number="editSupplier.cost"
                    label="Cost"
                  />
                </b-field>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot">
            <b-button
              size="is-small"
              label="Confirm"
              type="is-primary"
              :loading="editingSupplier"
              @click="confirmEditAssignedSupplier"
              :disabled="isEditSupplierSubmitDisabled"
            ></b-button>
            <b-button
              size="is-small"
              label="Cancel"
              type="is-danger"
              @click="isEditSupplierModalActive = false"
            ></b-button>
          </footer>
        </div>
      </template>
    </b-modal>
    <b-modal v-model="isAddSupplierModalActive" has-modal-card trap-focus aria-role="dialog" aria-modal>
      <template #default>
        <div class="modal-card">
          <header class="modal-card-head">Assign Supplier</header>
          <section class="modal-card-body">
            <div class="columns">
              <div class="column is-fullwidth">
                <b-field label="Search">
                  <b-autocomplete
                    v-model="suppliersQuery"
                    :data="suppliers"
                    :loading="isLoadingSuppliers"
                    icon="search"
                    open-on-focus
                    clearable
                    append-to-body
                    field="name"
                    @select="setSelectedSupplier"
                  >
                    <template #empty>No results found at all</template>
                  </b-autocomplete>
                </b-field>
                <b-field label="Description">
                  <b-input type="textarea" v-model="supplierMeta.description" label="Description" rows="3" required />
                </b-field>
                <b-field label="Sku">
                  <b-input v-model="supplierMeta.sku" label="Sku" required />
                </b-field>
                <b-field label="Cost" required>
                  <b-input
                    type="number"
                    step="0.01"
                    min="0"
                    icon="dollar-sign"
                    v-model.number="supplierMeta.cost"
                    label="Cost"
                  />
                </b-field>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot">
            <b-button
              size="is-small"
              label="Add"
              type="is-primary"
              @click="assignSupplier"
              :loading="assigningSupplier"
              :disabled="!isSelectedSupplierSet"
            ></b-button>
            <b-button
              size="is-small"
              label="Cancel"
              type="is-danger"
              @click="isAddSupplierModalActive = false"
            ></b-button>
          </footer>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import _ from 'lodash';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { CatalogService as catalogShared } from '@newmoon-org/shared';

import CatalogItemService from '@/service/catalog-items.service';
import ItemDetailService from '@/service/catalogItemDetails.service';
import { listSuppliers } from '@/service/suppliers.service';

import visibilityMixin from '@/mixins/visibility';

const DEFAULT_DISPLAY_CONFIG = {
  cost: 0,
  price: 0,
  employeeProductivityPrice: 0,
  mlhOverheadPrice: 0,
  localOverheadPrice: 0,
  laborBurdenPercent: 0,
  laborBurdenPrice: 0,
  marginPercentage: 0,
};

function isPermitType(item) {
  return item?.type === 'permit';
}

function toJsDate(value) {
  if (!value) return undefined;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

export default {
  name: 'CatalogItemEditPage',
  mixins: [visibilityMixin],
  data() {
    return {
      types: CatalogItemService.CATALOG_ITEM_TYPES,
      qualities: CatalogItemService.QUALITY,
      isAddSupplierModalActive: false,
      suppliersQuery: '',
      suppliers: [],
      selectedSupplier: null,
      isLoadingSuppliers: false,
      isAddNewActive: false,
      addNewType: '',
      addNewValue: '',
      supplierMeta: {},
      assigningSupplier: false,
      unassigningSupplier: false,
      isEditSupplierModalActive: false,
      editSupplier: {},
      editingSupplier: false,
      loadedCheckedIn: false,
      loadedIsActive: true,
      loadedVendorCost: 0,
      loadedCostToCustomer: 0,
      loadedVendorCostUpdatedDt: null,
      loadedCostToCustomerUpdatedDt: null,
    };
  },
  computed: {
    isMobile() {
      return this.$isMobile();
    },
    isAddNewValueValid() {
      return !!this.addNewValue;
    },
    cost() {
      const markupCost = catalogShared.computeLineItemPrice(this.item);
      return {
        cost: Number(this.item.priceConfiguration?.cost) || 0,
        markupCost,
      };
    },
    id() {
      return this.$route.params.catalogItemId;
    },
    isNew() {
      return this.id === 'new';
    },
    title() {
      return this.isNew ? 'Create New Item' : `${this.item.name}`;
    },
    isImportedItem() {
      return Boolean(this.item?.isImported);
    },
    canDelete() {
      return this.loadedIsActive === false;
    },
    headerLastUpdated() {
      if (this.isNew) return undefined;
      return toJsDate(this.item?.updatedAt || this.item?.createdAt);
    },
    vendorCostLastUpdated() {
      return toJsDate(this.loadedVendorCostUpdatedDt);
    },
    costToCustomerLastUpdated() {
      return toJsDate(this.loadedCostToCustomerUpdatedDt);
    },
    loading() {
      return this.$asyncComputed.item.updating;
    },
    isEditSupplierSubmitDisabled() {
      return false;
    },
    hasSupplier() {
      return Boolean(this.item?.supplier?.id);
    },
    displaySuppliers() {
      if (!this.hasSupplier) return [];
      const c = this.item.supplier;
      return [
        {
          id: c.id,
          name: c.name,
          sku: c.sku,
          cost: c.cost,
          description: c.description,
          pricedAt: c.pricedAt,
        },
      ];
    },
    isFpv() {
      return this.item.type === 'fpv';
    },
    isAppliance() {
      return this.item.type === 'appliance';
    },
    isFixture() {
      return this.item.type === 'fixture';
    },
    isPermit() {
      return isPermitType(this.item);
    },
    isLabor() {
      return String(this.item.type ?? '').toLowerCase() === 'labor';
    },
    isEquipment() {
      return this.item.type === 'equipment';
    },
    isSelectedSupplierSet() {
      const costNum = Number(this.supplierMeta.cost);
      return (
        !_.isEmpty(this.selectedSupplier) &&
        !_.isEmpty(this.supplierMeta.sku) &&
        Number.isFinite(costNum) &&
        !_.isEmpty(this.supplierMeta.description)
      );
    },
    currentTypeFields() {
      return ItemDetailService.TYPE_DEFINITIONS[this.item.type]?.fields ?? [];
    },
  },
  watch: {
    suppliersQuery(val) {
      if (_.isEmpty(val)) {
        this.searchSuppliers();
        return;
      }
      this.searchSuppliersDebounced();
    },
    'item.type': {
      handler(newType, oldType) {
        if (!newType) return;
        this.$asyncComputed.detail.update();
        if (this.isNew || (oldType && oldType !== newType)) {
          this.$set(this.item, 'priceConfiguration', {
            ...DEFAULT_DISPLAY_CONFIG,
            marginPercentage: isPermitType(this.item) ? 100 : 150,
          });
        }
      },
    },
  },
  created() {
    this.searchSuppliersDebounced = _.debounce(this.searchSuppliers, 300);
  },
  mounted() {
    this.searchSuppliers();
  },
  asyncComputed: {
    item: {
      get() {
        if (this.isNew) {
          this.loadedIsActive = true;
          return Promise.resolve({
            suppliers: [],
            isActive: true,
            isTaxable: true,
            checkedIn: false,
            type: CatalogItemService.CATALOG_ITEM_TYPES[0].value,
            priceConfiguration: { ...DEFAULT_DISPLAY_CONFIG, marginPercentage: 150 },
          });
        }
        return CatalogItemService.getById(this.id).then(item => {
          const stored = {
            ...(item.priceConfiguration ?? {}),
            costInCents: item.priceConfiguration?.costInCents ?? item.cost,
            marginPercentage: item.priceConfiguration?.marginPercentage ?? item.marginPercentage,
          };
          const display = { ...DEFAULT_DISPLAY_CONFIG, ...catalogShared.toDisplayConfig(stored) };
          if (isPermitType(item)) display.marginPercentage = 100;
          const checkedIn = Boolean(item.checkedIn);
          this.loadedCheckedIn = checkedIn;
          this.loadedIsActive = item.isActive ?? true;
          this.loadedVendorCost = item.priceConfiguration?.costInCents ?? item.cost ?? 0;
          this.loadedCostToCustomer = item.priceConfiguration?.priceInCents ?? item.costToCustomer ?? 0;
          this.loadedVendorCostUpdatedDt = item.priceConfiguration?.vendorCostUpdatedDt ?? null;
          this.loadedCostToCustomerUpdatedDt = item.priceConfiguration?.costToCustomerUpdatedDt ?? null;
          return {
            suppliers: [],
            ...item,
            isActive: item.isActive ?? true,
            checkedIn,
            cost: (item.cost / 100).toFixed(2),
            priceConfiguration: display,
          };
        });
      },
      default: {
        suppliers: [],
        priceConfiguration: { ...DEFAULT_DISPLAY_CONFIG },
      },
    },
    detail: {
      async get() {
        const defaultValue = this.item.type
          ? ItemDetailService.TYPE_DEFINITIONS[this.item.type]?.defaultValue ?? {}
          : {};
        return this.item.type
          ? ItemDetailService.getById(this.item.type).then(r => ({
              ...defaultValue,
              ...r,
            }))
          : Promise.resolve(defaultValue);
      },
      default: {},
    },
  },
  methods: {
    withCheckedInUpdatedDt(item) {
      const checkedIn = Boolean(item.checkedIn);
      const changed = this.isNew ? checkedIn : checkedIn !== Boolean(this.loadedCheckedIn);
      if (!changed) return item;
      return {
        ...item,
        checkedInUpdatedDt: firebase.firestore.FieldValue.serverTimestamp(),
      };
    },
    applyPricingUpdatedDts(storagePc) {
      const pc = { ...storagePc };
      const vendorChanged = this.isNew ? pc.costInCents !== 0 : pc.costInCents !== this.loadedVendorCost;
      const customerChanged = this.isNew ? pc.priceInCents !== 0 : pc.priceInCents !== this.loadedCostToCustomer;
      if (vendorChanged) {
        pc.vendorCostUpdatedDt = firebase.firestore.FieldValue.serverTimestamp();
      }
      if (customerChanged) {
        pc.costToCustomerUpdatedDt = firebase.firestore.FieldValue.serverTimestamp();
      }
      return pc;
    },
    formatSupplierPricedAt(ts) {
      if (!ts) return '';
      if (typeof ts.toDate === 'function') return ts.toDate().toLocaleDateString();
      if (ts instanceof Date) return ts.toLocaleDateString();
      return String(ts);
    },
    async searchSuppliers() {
      this.isLoadingSuppliers = true;
      try {
        const supplierList = await listSuppliers({ page: 0, size: 1000 });
        if (!this.suppliersQuery) {
          this.suppliers = supplierList;
          return;
        }
        this.suppliers = supplierList.filter(
          s => s.name.toLowerCase().indexOf(this.suppliersQuery.toLowerCase()) !== -1
        );
      } catch (e) {
        this.$buefy.toast.open({
          message: e.message,
          type: 'is-danger',
        });
      } finally {
        this.isLoadingSuppliers = false;
      }
    },
    setSelectedSupplier(supplier) {
      this.selectedSupplier = { ...supplier, cost: 0 };
    },
    onEditSupplier(supplier) {
      this.editSupplier = {
        ...supplier,
        cost: this.supplierCostNumber(supplier.cost),
      };
      this.isEditSupplierModalActive = true;
    },
    supplierCostNumber(value) {
      if (value === '' || value == null) return undefined;
      const n = Number(value);
      return Number.isFinite(n) ? n : undefined;
    },
    resetSupplierModal() {
      this.selectedSupplier = null;
      this.supplierMeta = {};
      this.suppliersQuery = '';
      this.isAddSupplierModalActive = false;
    },
    async assignSupplier() {
      const supplier = {
        id: this.selectedSupplier.id,
        name: this.selectedSupplier.name,
        ...this.supplierMeta,
        cost: this.supplierCostNumber(this.supplierMeta.cost),
      };
      if (this.isNew) {
        this.$set(this.item, 'supplier', supplier);
        this.resetSupplierModal();
        return;
      }
      try {
        this.assigningSupplier = true;
        await CatalogItemService.setSupplier(this.id, supplier);
        this.$asyncComputed.item.update();
      } catch (e) {
        this.$buefy.toast.open({
          message: e.message,
          type: 'is-danger',
        });
      } finally {
        this.assigningSupplier = false;
        this.resetSupplierModal();
      }
    },
    async unassignSupplier() {
      if (this.isNew) {
        this.$set(this.item, 'supplier', null);
        return;
      }
      try {
        this.unassigningSupplier = true;
        await CatalogItemService.clearSupplier(this.id);
        this.$asyncComputed.item.update();
      } catch (e) {
        this.$buefy.toast.open({
          message: e.message,
          type: 'is-danger',
        });
      } finally {
        this.unassigningSupplier = false;
      }
    },
    async confirmEditAssignedSupplier() {
      if (this.isNew) {
        this.$set(this.item, 'supplier', {
          ...this.item.supplier,
          ...this.editSupplier,
          cost: this.supplierCostNumber(this.editSupplier.cost),
        });
        this.isEditSupplierModalActive = false;
        return;
      }
      try {
        this.editingSupplier = true;
        await CatalogItemService.editAssignedSupplier(this.id, this.editSupplier.id, {
          ...this.editSupplier,
          cost: this.supplierCostNumber(this.editSupplier.cost),
        });
        this.$asyncComputed.item.update();
      } catch (e) {
        this.$buefy.toast.open({
          message: e.message,
          type: 'is-danger',
        });
      } finally {
        this.editingSupplier = false;
        this.isEditSupplierModalActive = false;
      }
    },
    async save(e) {
      e.preventDefault();
      const displayPc = this.item.priceConfiguration ?? { ...DEFAULT_DISPLAY_CONFIG };
      const priceInDollars = catalogShared.computeLineItemPrice(this.item);
      const storagePc = this.applyPricingUpdatedDts(
        catalogShared.toStorageConfig({ ...displayPc, price: priceInDollars })
      );
      const item = {
        ...this.item,
        cost: storagePc.costInCents,
        costToCustomer: storagePc.priceInCents,
        priceConfiguration: storagePc,
      };
      const payload = this.withCheckedInUpdatedDt(item);

      const fn = this.isNew
        ? () => CatalogItemService.create(payload)
        : () => CatalogItemService.update(this.id, payload);
      try {
        const saved = await fn();
        if (this.isNew && this.item.supplier?.id) {
          await CatalogItemService.setSupplier(saved.id, this.item.supplier);
        }
        this.$buefy.toast.open({
          message: 'Successfully saved Item',
          type: 'is-success',
        });
        await this.goBack();
      } catch (e) {
        this.$buefy.notification.open({
          message: e.message,
          type: 'is-danger',
        });
      }
    },
    deleteItem() {
      if (!this.canDelete) return;
      this.$buefy.dialog.confirm({
        title: 'Delete Catalog Item',
        message: `Are you sure you want to delete <b>${this.item.name}</b>? This action cannot be undone.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.confirmDelete(),
      });
    },
    async confirmDelete() {
      try {
        await CatalogItemService.softDelete(this.id);
        this.$buefy.toast.open({
          message: 'Catalog item deleted',
          type: 'is-success',
        });
        await this.goBack();
      } catch (e) {
        this.$buefy.notification.open({
          message: e.message,
          type: 'is-danger',
        });
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    async closeModal(isAdding = false) {
      this.isAddNewActive = false;
      if (isAdding) {
        this.detail[this.addNewType] = Array.from(new Set([...this.detail[this.addNewType], this.addNewValue])).sort();
        await ItemDetailService.update(this.detail.id, this.detail).then(() => this.$asyncComputed.detail.update());
      }
      this.addNewValue = null;
      this.addNewType = '';
    },
    openModal(type) {
      this.isAddNewActive = true;
      this.addNewType = type;
    },
  },
};
</script>

<style scoped lang="scss">
#catalog-items-edit {
  .catalog-item-edit__title-mark {
    position: relative;
    display: inline-block;
  }
  .catalog-item-edit__import-wrap {
    position: absolute;
    left: 100%;
    margin-left: 0.18em;
    top: 0.12em;
    line-height: 1;
  }
  .catalog-item-edit__import-icon {
    cursor: default;
    vertical-align: top;
  }
  .catalog-item-edit__last-updated {
    font-size: 0.75rem;
    color: #7a7a7a;
    margin-top: 0.25rem;
  }
  .catalog-item-edit__last-updated--header {
    margin-top: -1.25rem;
    margin-bottom: 1rem;
  }
  .field-name {
    font-weight: bold;
    margin-right: 1em;
  }

  .grouping {
    border: #ddd solid 1px;
    border-radius: 5px;
  }

  .is-odd {
    background-color: #eff1fa;
  }

  .supplier-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 0.5em;
    align-items: center;
  }
}
</style>
